import React, { useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { useFormik } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import { Footer } from '../Footer';
import { RootStates } from 'redux/store';
import { useUpdateUserDetailsMutation } from 'redux/services/auth';
import { useDispatch, useSelector } from 'react-redux';
import { Loader } from 'components/common/Loader';
import { saveUserDetails } from 'redux/authSlice';
import { getHeaders } from 'helper/helper';
import type { CompanyNature } from 'redux/authSlice';
import toast from 'react-hot-toast';
import Modal from 'components/common/Modal/Modal';
import FormInput from 'components/FormInput';
import { useTranslation } from 'react-i18next';
import { routesPaths } from 'constants/paths';
import './OrganizationForm.scss';

export const OrganizationForm: React.FC<{}> = ({ children }) => {
  const { t } = useTranslation();
  const { page } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [otherValue, setOtherValue] = useState('');
  const [showOtherValueDialog, setShowOtherValueDialog] = useState(false);
  const [updateUserDetails, { isLoading }] = useUpdateUserDetailsMutation();
  const user = useSelector((state: RootStates) => state.auth.userDetails);

  const getDefaultCompanySize = () => {
    if (user.onboardingData?.companySize) {
      return `${user.onboardingData.companySize.lower}-${user.onboardingData.companySize.upper}`;
    }

    return '';
  };

  useEffect(() => {
    return () => {
      setOtherValue('');
    };
  }, []);

  const formik = useFormik({
    initialValues: {
      companyNature:
        user.onboardingData?.companyNature ?? ({} as CompanyNature),
      companySize: getDefaultCompanySize(),
    },
    onSubmit: async (values) => {
      const headers = await getHeaders();
      const payload: any = await updateUserDetails({
        headers: headers,
        body: {
          onboardingData: {
            ...user.onboardingData,
            lastPageFilled: page,
            companySize: values.companySize
              ? {
                  upper: Number(values.companySize.split('-')?.[1]),
                  lower: Number(values.companySize.split('-')?.[0]),
                }
              : undefined,
            companyNature: values.companyNature || '',
            isCompleted: true,
          },
        },
      });

      if (payload.data) {
        dispatch(saveUserDetails(payload.data));
        navigate(routesPaths.appDemo, { replace: true });
      } else {
        toast.error(t('error_project_update_failed'));
      }
    },
  });

  const getSelectCompanyNatureHandler = (nature: CompanyNature) => () => {
    if (nature.value === '') {
      setShowOtherValueDialog(true);
    }
    formik.setFieldValue('companyNature', nature);
  };

  const handleSelect = (event: React.ChangeEvent<HTMLSelectElement>) => {
    formik.setFieldValue('companySize', event.target.value.trim());
  };

  const handleBack = () => {
    if (
      user.onboardingData?.isEmailVerified &&
      user.onboardingData?.isMobileVerified
    ) {
      navigate('/onboarding/0');
    } else {
      navigate('/onboarding/1');
    }
  };

  const handleCloseOtherCompanyNatureDialog = () => {
    formik.setFieldValue('companyNature', {});
    setShowOtherValueDialog(false);
  };

  const handleSubmitOtherCompanyNatureDialog = () => {
    formik.setFieldValue('companyNature', {
      ...formik.values.companyNature,
      value: otherValue,
    });
    setShowOtherValueDialog(false);
  };

  const handleChangeOtherCompanyNatureInput = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setOtherValue(event.target.value);
  };

  return (
    <div className="organization-form">
      <h5>{t('onboarding_header3')}</h5>
      <Form onSubmit={formik.handleSubmit}>
        <div className="form-content">
          <div className="company-size-input">
            <label>{t('label_company_size')}</label>
            <select
              className="form-select"
              value={formik.values.companySize}
              onChange={handleSelect}
            >
              <option> </option>
              {user.defaultOptions?.companySize.map(({ lower, upper }) => (
                <option key={`${lower}-${upper}`} value={`${lower}-${upper}`}>
                  {`${lower}-${upper}`}
                </option>
              ))}
            </select>
            {formik.errors.companySize && (
              <span className="error">{`*${formik.errors.companySize}`}</span>
            )}
          </div>
          <div className="additional-info-container">
            <span>{t('onboardng_additional_info')}</span>
            <div className="btn-container">
              {user.defaultOptions?.companyNature.map((val) => (
                <Button
                  key={val.category}
                  className={`btn ${
                    formik.values.companyNature?.value === val.value ||
                    formik.values.companyNature?.category === val.category
                      ? 'active'
                      : ''
                  }`}
                  onClick={getSelectCompanyNatureHandler(val)}
                >
                  {val.value === '' && otherValue ? otherValue : val.category}
                </Button>
              ))}
              {formik.errors.companyNature && (
                <span className="error">
                  {t('onboarding_select_company_nature')}
                </span>
              )}
            </div>
          </div>
        </div>
        <Footer page={Number(page!)} onBack={handleBack}>
          {children}
        </Footer>
        <Modal
          show={showOtherValueDialog}
          submitBtnText={t('btn_ok')}
          modalClassName="other-value-popup"
          title={t('onboarding_company_nature_title')}
          footerContentAlignment="center"
          onClose={handleCloseOtherCompanyNatureDialog}
          onSubmit={handleSubmitOtherCompanyNatureDialog}
        >
          <FormInput
            name="other"
            value={otherValue}
            label={t('label_company_nature')}
            onChange={handleChangeOtherCompanyNatureInput}
          />
        </Modal>
      </Form>
      {isLoading && <Loader />}
    </div>
  );
};
